/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChange,
  Input,
  Output,
  forwardRef,
  ViewChild,
  EventEmitter,
  ElementRef,
  AfterViewInit,
  Injector,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
/***********************/
/* BFX */
import * as CubiqTS from "shared/cubiq-model-multi-asset";
// models
import { ICNInputControlState } from "./models/bfx-interface-controls.enums";

export const KEYBOARD_SPACE_KEY_CODE = 32;
export const KEYBOARD_ENTER_KEY_CODE = 13;

export const CLIENT_INTENT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BfxClientIntentToggleComponent),
  multi: true,
};

import { LoggerService } from "@bfx/logger";
import { SizeMixin } from "shared/base-components/mixins/size";
import { EmphasisMixin } from "shared/base-components/mixins/emphasis";
import { InputLabelStateMixin } from "shared/base-components/mixins/label-state";
import { DisabledMixin } from "shared/base-components/mixins/disabled";
import { ComponentMixin } from "shared/base-components/mixins/mixin";
import { BfxInputDirective } from "shared/base-components/bfx-input";
const logger = LoggerService.for(__dirname);

const Mixins = SizeMixin(EmphasisMixin(InputLabelStateMixin(DisabledMixin(ComponentMixin))));

@Component({
  selector: "bfx-client-intent-toggle",
  templateUrl: "./html/bfx-toggle-client-intent.html",
  providers: [CLIENT_INTENT_VALUE_ACCESSOR],
  standalone: false,
})
export class BfxClientIntentToggleComponent
  extends Mixins
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Input() inputId: string;

  @Input("label") input_label: string;

  @Input("disabled") input_disabled: boolean;

  //@Input('intent') input_intent: boolean | string;

  @Input("ccy") input_ccy: string; // just so that the intent value is updated on ccy change

  @ViewChild("inputField", { static: true }) inputField: ElementRef;
  @ViewChild("intentValueButton", { static: true })
  intentValueButton: ElementRef;

  onModelChange: Function = (value: any) => {};
  onModelTouched: Function = () => {};

  /* External Validation - not integrated in code */
  input_externalState: ICNInputControlState;

  @Input()
  get externalState() {
    return this.input_externalState;
  }

  set externalState(value: any) {
    this.input_externalState = value;
  }

  /***************************************/
  /* DATA */
  public config: any = {};

  public data: any = {
    label: "",
    value: null, // CubiqTS.OrderIntent
    disabled: false,
    intentValue: false, // boolean => true = 0 = buy, false = 1 = sell,
    buttonLabel: "",
    template: {
      buy: "",
      buyLabelKey: "interface_control.client_intent.buy",
      buyValue: CubiqTS.OrderIntent.BUY, // 0
      sell: "",
      sellLabelKey: "interface_control.client_intent.sell",
      sellValue: CubiqTS.OrderIntent.SELL, // 1
    },
  };

  public locked: boolean = false;

  /**********************************************************************/

  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    injector: Injector,
    public element: ElementRef,
    public translationService: TranslateService,
    public inputDirective: BfxInputDirective,
  ) {
    super(injector);
    this.size = "sm";
  }

  ngOnInit() {
    this.initialize();
  }

  ngOnDestroy() {}

  ngOnChanges() {
    // changes: {[propKey: string]: SimpleChange}
    this.initialize();
  }

  /**********************************************************************/

  /* ControlValueAccessor */

  writeValue(value: any): void {
    if (
      value != null &&
      (value === CubiqTS.OrderIntent.BUY || value === CubiqTS.OrderIntent.SELL)
    ) {
      if (value === CubiqTS.OrderIntent.BUY) this.setClientIntentToggleState(false);
      else if (value === CubiqTS.OrderIntent.SELL) this.setClientIntentToggleState(true);
    } else {
      this.setClientIntentToggleState(false);
    }
    this.onModelChange(this.data.value);
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  public onChange(event: any): void {
    this.onModelChange(this.data.value);
  }

  /**********************************************************************/

  /* INITIALIZATION */
  protected initialize(): void {
    this.initializeLabelTranslation();
    // set label
    if (this.input_label) {
      this.data.label = this.input_label;
    }
    // set disabled
    if (this.input_disabled != null) {
      this.data.disabled = this.input_disabled;
    }

    /* 		if(this.input_intent){
                    if(typeof this.input_intent === 'boolean'){
                        this.setClientIntentToggleState( this.input_intent);
                    }else if((typeof this.input_intent === 'string' && this.input_intent.toUpperCase() === 'BUY') || (typeof this.input_intent === 'number' && this.input_intent === CubiqTS.OrderIntent.BUY)){
                        this.setClientIntentToggleState(false);
                    }else if((typeof this.input_intent === 'string' && this.input_intent.toUpperCase() === 'SELL') || (typeof this.input_intent === 'number' && this.input_intent === CubiqTS.OrderIntent.SELL)){
                        this.setClientIntentToggleState(true);
                    }
                }else{//default
                    this.setClientIntentToggleState(false);
                } */
    if (
      this.data.value != null &&
      (this.data.value === CubiqTS.OrderIntent.BUY || this.data.value === CubiqTS.OrderIntent.SELL)
    ) {
      if (this.data.value === CubiqTS.OrderIntent.BUY) this.setClientIntentToggleState(false);
      else if (this.data.value === CubiqTS.OrderIntent.SELL) this.setClientIntentToggleState(true);
    } else {
      this.data.value = CubiqTS.OrderIntent.BUY;
      this.setClientIntentToggleState(false);
    }
    //this.onChange(null);
  }

  protected initializeLabelTranslation(): void {
    this.data.template.buy = this.translationService.instant(this.data.template.buyLabelKey);
    this.data.template.sell = this.translationService.instant(this.data.template.sellLabelKey);
  }

  /**********************************************************************/

  /* PUBLIC METHODS */
  public handleIntentChange(nextState: boolean): void {
    this.setClientIntentToggleState(nextState);
    //this.onChange(null);
  }

  public handleKeyDownEvent(event: any): void {
    switch (event.keyCode) {
      case KEYBOARD_ENTER_KEY_CODE:
        event.preventDefault();
        break;
    }
  }

  public handleKeyUpEvent(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    switch (event.keyCode) {
      case KEYBOARD_SPACE_KEY_CODE:
        setTimeout(() => this.intentValueButton.nativeElement.focus());
        break;
    }
  }

  public resetInputControl() {
    this.initializeLabelTranslation();

    this.data.label = this.input_label != null && this.input_label !== "" ? this.input_label : "";
    this.data.value = CubiqTS.OrderIntent.BUY;
    this.data.disabled = false;
    this.data.intentValue = false; // false = 0 = buy, true = 1 = sell,
    this.data.buttonLabel = this.data.template.buy;

    this.initialize();
  }

  public lockInputControl(): void {
    this.setLocked(true);
  }

  public unlockInputControl(): void {
    this.setLocked(false);
  }

  private setLocked(value: boolean) {
    this.locked = value;
    this.disabled = value;
    this.inputLabelState = value ? "disabled" : "enabled";
  }

  public focusInput() {
    this.inputField.nativeElement.focus();
  }

  /**********************************************************************/

  /* PRIVATE METHODS */

  private setClientIntentToggleState(state: boolean): void {
    if (state) {
      // sell
      this.data.intentValue = true;
      this.data.value = this.data.template.sellValue;
      this.data.buttonLabel = this.data.template.sell;
    } else {
      // buy
      this.data.intentValue = false;
      this.data.value = this.data.template.buyValue;
      this.data.buttonLabel = this.data.template.buy;
    }
    this.onChange(null);
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }
}
